import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import ReactFlow, { 
  Controls, 
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import html2canvas from 'html2canvas';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import api from '../lib/api';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Download, 
  Loader2, 
  Mail,
  Phone,
  Building2,
  MapPin,
  FileImage,
  FileText,
  FileSpreadsheet,
  ChevronDown,
  Move,
  Check,
  Info,
  User,
  Heart,
  Calendar,
  Briefcase,
  UserCircle,
  Hash,
  QrCode,
  X,
  Users
} from 'lucide-react';

// Custom node component with drag handle
const EmployeeNode = ({ data, dragging }) => {
  const getInitials = () => {
    return `${data.firstName?.[0] || ''}${data.lastName?.[0] || ''}`.toUpperCase();
  };

  const primaryColor = data.primaryColor || '#2054A6';
  const accentColor = data.accentColor || '#F97316';
  // Couleur spécifique à la branche/département (style exigé par le client)
  const branchColor = data.branchColor || primaryColor;
  const branchBg = data.branchBg || `${branchColor}15`;

  return (
    <div 
      className={`rounded-xl border-2 shadow-md px-4 pt-4 pb-8 w-[230px] min-h-[200px] transition-all cursor-grab active:cursor-grabbing relative overflow-hidden ${
        dragging 
          ? 'shadow-2xl scale-105' 
          : 'hover:shadow-xl'
      }`}
      style={{ 
        borderColor: dragging ? branchColor : '#e2e8f0',
        backgroundColor: branchBg,
      }}
      onClick={() => data.onSelect?.(data)}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = branchColor; }}
      onMouseLeave={(e) => { if (!dragging) e.currentTarget.style.borderColor = '#e2e8f0'; }}
    >
      {/* Handles pour que ReactFlow puisse dessiner les edges */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0, background: 'transparent' }} isConnectable={false} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, background: 'transparent' }} isConnectable={false} />
      <div className="flex flex-col items-center text-center">
        <Avatar className="h-16 w-16 mb-3 ring-4 ring-white shadow">
          {data.photoUrl ? (
            <AvatarImage src={data.photoUrl} alt={`${data.firstName} ${data.lastName}`} />
          ) : null}
          <AvatarFallback 
            className="text-white text-lg font-semibold"
            style={{ backgroundColor: branchColor }}
          >
            {getInitials()}
          </AvatarFallback>
        </Avatar>
        <p className="font-semibold text-slate-900 text-sm leading-tight" title={`${data.firstName} ${data.lastName}`}>
          {data.firstName} {data.lastName}
        </p>
        <p 
          className="text-xs font-medium mt-1 px-1 leading-tight"
          style={{ color: branchColor }}
          title={data.jobTitle}
        >
          {data.jobTitle}
        </p>
        {/* Hidden subordinates indicator */}
        {data.hiddenSubordinates > 0 && (
          <div 
            className="mt-2 flex items-center gap-1 text-xs text-white px-2 py-1 rounded-full"
            style={{ backgroundColor: accentColor }}
          >
            <Users className="w-3 h-3" />
            +{data.hiddenSubordinates}
          </div>
        )}
      </div>
      {/* Bande colorée département en bas */}
      {data.department && (
        <div 
          className="absolute bottom-0 left-0 right-0 py-1.5 text-[10px] font-semibold text-white text-center truncate px-2"
          style={{ backgroundColor: branchColor }}
          title={data.department}
        >
          {data.department}
        </div>
      )}
      {/* Drag indicator */}
      <div className="absolute top-2 right-2 text-slate-400">
        <Move className="w-3 h-3" />
      </div>
    </div>
  );
};

// Composant pour les nœuds département (mode regroupement)
const DepartmentNode = ({ data }) => {
  const primaryColor = data.primaryColor || '#2054A6';
  const bg = data.branchBg || '#f8fafc';

  return (
    <div
      className="rounded-xl border-2 shadow-md p-4 w-[210px] text-center relative overflow-hidden"
      style={{ borderColor: primaryColor, backgroundColor: bg }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, background: 'transparent' }} isConnectable={false} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, background: 'transparent' }} isConnectable={false} />
      <div
        className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center"
        style={{ backgroundColor: `${primaryColor}25` }}
      >
        <Building2 className="w-6 h-6" style={{ color: primaryColor }} />
      </div>
      <p className="font-bold text-slate-900">{data.name}</p>
      <Badge 
        className="mt-2 mb-4 text-xs"
        style={{ backgroundColor: primaryColor, color: 'white' }}
      >
        {data.count} employé{data.count > 1 ? 's' : ''}
      </Badge>
      <div
        className="absolute bottom-0 left-0 right-0 h-2"
        style={{ backgroundColor: primaryColor }}
      />
    </div>
  );
};

const nodeTypes = {
  employee: EmployeeNode,
  department: DepartmentNode,
};

function OrgChartFlow({ 
  employees, 
  departments, 
  sites,
  societes = [],
  filters, 
  setSelectedEmployee,
  onManagerChange,
  branding = { primaryColor: '#2054A6', accentColor: '#F97316' },
  customPositions = {},
  onCustomPositionsChange,
  onResetLayout,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const reactFlowInstance = useReactFlow();
  const flowRef = useRef(null);
  // Ref pour lire les positions custom sans retrigger le layout à chaque save
  const customPositionsRef = useRef(customPositions);
  useEffect(() => { customPositionsRef.current = customPositions || {}; }, [customPositions]);

  const getDepartmentName = useCallback((deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || '';
  }, [departments]);

  const getSiteName = useCallback((siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || '';
  }, [sites]);

  const getSocieteName = useCallback((societeId) => {
    const societe = societes.find(s => s.id === societeId);
    return societe?.name || '';
  }, [societes]);

  const buildOrgChart = useCallback(() => {
    // Helper : applique les positions custom (drag & drop sauvegardé) par-dessus
    // celles calculées par dagre / layout auto.
    const applyCustomPositions = (list) => {
      const cp = customPositionsRef.current || {};
      if (!cp || Object.keys(cp).length === 0) return list;
      return list.map((n) => {
        const saved = cp[n.id];
        if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
          return { ...n, position: { x: saved.x, y: saved.y } };
        }
        return n;
      });
    };
    const commitNodes = (list) => setNodes(applyCustomPositions(list));

    // Utilise UNIQUEMENT les couleurs de la charte graphique du client.
    // Pas de coloration différente par département : toutes les branches partagent
    // la couleur primaire de l'entreprise (seule la hiérarchie structurelle est différenciante).
    const primary = branding.primaryColor || '#2054A6';
    // Version très claire du primary pour le fond des cartes (hex + "15" = ~8% opacité)
    const primaryBg = `${primary}12`;
    const getBranchStyle = () => ({ color: primary, bg: primaryBg });

    // Filter employees
    let filteredEmployees = employees.filter(e => e.status === 'Actif');
    
    if (filters.departmentId) {
      filteredEmployees = filteredEmployees.filter(e => e.departmentId === filters.departmentId);
    }
    if (filters.siteId) {
      filteredEmployees = filteredEmployees.filter(e => e.siteId === filters.siteId);
    }
    if (filters.societeId) {
      filteredEmployees = filteredEmployees.filter(e => e.societeId === filters.societeId);
    }
    // Filtre par niveau hiérarchique
    if (filters.hierarchyLevel > 0) {
      filteredEmployees = filteredEmployees.filter(e => e.hierarchyLevel === filters.hierarchyLevel);
    }

    // Build hierarchy
    const employeeMap = new Map(filteredEmployees.map(e => [e.id, e]));
    const rootEmployees = filteredEmployees.filter(e => !e.managerId || !employeeMap.has(e.managerId));

    // Calculate positions using tree layout
    const nodeSpacing = { x: 250, y: 150 };
    const newNodes = [];
    const newEdges = [];
    const maxDepthLimit = filters.maxDepth || 0; // 0 = unlimited

    // Déterminer le mode d'affichage
    // - Si filtre par niveau hiérarchique → mode grille par niveau
    // - Si beaucoup de racines sans filtre département → regrouper par département
    // - Si filtre département actif mais trop de racines → mode grille simple
    // - Sinon → mode hiérarchique classique
    const useLevelGrid = filters.hierarchyLevel > 0;
    const useDepartmentGrouping = !useLevelGrid && rootEmployees.length > 10 && !filters.departmentId && !filters.siteId && !filters.societeId;
    const useSimpleGrid = !useLevelGrid && rootEmployees.length > 10 && (filters.departmentId || filters.siteId || filters.societeId);

    // MODE GRILLE PAR NIVEAU HIÉRARCHIQUE
    if (useLevelGrid) {
      const empPerRow = 5;  // Reduced for better readability
      
      // Trier les employés par département puis par nom
      const sortedEmployees = [...filteredEmployees].sort((a, b) => {
        const deptA = getDepartmentName(a.departmentId);
        const deptB = getDepartmentName(b.departmentId);
        if (deptA !== deptB) return deptA.localeCompare(deptB);
        return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
      });

      sortedEmployees.forEach((emp, index) => {
        const row = Math.floor(index / empPerRow);
        const col = index % empPerRow;
        
        newNodes.push({
          id: emp.id,
          type: 'employee',
          position: { x: col * 240, y: row * 170 },  // Increased spacing
          draggable: true,
          data: {
            ...emp,
            department: getDepartmentName(emp.departmentId),
            site: getSiteName(emp.siteId),
            societe: getSocieteName(emp.societeId),
            onSelect: setSelectedEmployee,
            primaryColor: branding.primaryColor,
            accentColor: branding.accentColor,
              branchColor: getBranchStyle().color,
              branchBg: getBranchStyle().bg,
            hiddenSubordinates: 0
          }
        });
      });

      commitNodes(newNodes);
      setEdges(newEdges);
      return;
    }

    // MODE HIÉRARCHIQUE (dagre) : vraie arborescence top-down avec auto-layout.
    // Pas de nœud "Département" au milieu — le nom du département est affiché
    // en titre au-dessus du canvas (voir le H2 plus bas).
    if (useSimpleGrid) {
      const filteredIds = new Set(filteredEmployees.map(e => e.id));
      const spacingFactor = filters.spacing || 1;
      const NODE_WIDTH = 230;
      const NODE_HEIGHT = 240;

      // Construction du graphe dagre
      const g = new dagre.graphlib.Graph();
      g.setGraph({
        rankdir: 'TB',
        nodesep: Math.round(40 * spacingFactor),
        ranksep: Math.round(90 * spacingFactor),
        marginx: 40,
        marginy: 20,
      });
      g.setDefaultEdgeLabel(() => ({}));

      // Ajouter tous les nœuds avec leur taille
      filteredEmployees.forEach(emp => {
        g.setNode(emp.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
      });

      // Ajouter les edges (manager → employé) — uniquement si les 2 sont dans le filtre
      filteredEmployees.forEach(emp => {
        if (emp.managerId && filteredIds.has(emp.managerId)) {
          g.setEdge(emp.managerId, emp.id);
        }
      });

      // Exécuter l'algo
      dagre.layout(g);

      // Créer les nœuds React Flow
      const childrenByParent = new Map();
      filteredEmployees.forEach(emp => {
        if (emp.managerId && filteredIds.has(emp.managerId)) {
          if (!childrenByParent.has(emp.managerId)) childrenByParent.set(emp.managerId, []);
          childrenByParent.get(emp.managerId).push(emp.id);
        }
      });

      filteredEmployees.forEach(emp => {
        const nodeWithPosition = g.node(emp.id);
        if (!nodeWithPosition) return;
        const branch = getBranchStyle();
        const childCount = (childrenByParent.get(emp.id) || []).length;

        newNodes.push({
          id: emp.id,
          type: 'employee',
          // dagre renvoie le centre du nœud → convertir en top-left pour React Flow
          position: {
            x: nodeWithPosition.x - NODE_WIDTH / 2,
            y: nodeWithPosition.y - NODE_HEIGHT / 2,
          },
          draggable: true,
          data: {
            ...emp,
            department: getDepartmentName(emp.departmentId),
            site: getSiteName(emp.siteId),
            societe: getSocieteName(emp.societeId),
            onSelect: setSelectedEmployee,
            primaryColor: branding.primaryColor,
            accentColor: branding.accentColor,
            branchColor: branch.color,
            branchBg: branch.bg,
            hiddenSubordinates: 0,
            directReports: childCount,
          },
        });

        // Edges : uniquement les relations manager→employé. Pas de racine département.
        if (emp.managerId && filteredIds.has(emp.managerId)) {
          newEdges.push({
            id: `edge-mgr-${emp.managerId}-${emp.id}`,
            source: emp.managerId,
            target: emp.id,
            type: 'step',
            style: { stroke: branch.color, strokeWidth: 2.5 },
            markerEnd: { type: MarkerType.ArrowClosed, color: branch.color },
          });
        }
      });

      commitNodes(newNodes);
      setEdges(newEdges);
      return;
    }

    // MODE REGROUPEMENT PAR DÉPARTEMENT
    if (useDepartmentGrouping) {
      // Mode regroupement par département
      const employeesByDept = new Map();
      const employeesWithoutDept = [];

      filteredEmployees.forEach(emp => {
        if (emp.departmentId) {
          if (!employeesByDept.has(emp.departmentId)) {
            employeesByDept.set(emp.departmentId, []);
          }
          employeesByDept.get(emp.departmentId).push(emp);
        } else {
          employeesWithoutDept.push(emp);
        }
      });

      let currentX = 0;
      const deptNodeSpacing = 350; // Increased spacing between departments
      const empPerRow = 4; // Fewer employees per row for better readability

      // Créer un nœud pour chaque département
      const sortedDepts = Array.from(employeesByDept.entries())
        .sort((a, b) => {
          const deptA = departments.find(d => d.id === a[0]);
          const deptB = departments.find(d => d.id === b[0]);
          return (deptA?.name || '').localeCompare(deptB?.name || '');
        });

      sortedDepts.forEach(([deptId, deptEmployees], deptIndex) => {
        const dept = departments.find(d => d.id === deptId);
        const deptName = dept?.name || 'Sans département';
        const branch = getBranchStyle();

        // Calculer la largeur nécessaire pour ce département
        const rows = Math.ceil(deptEmployees.length / empPerRow);
        const deptWidth = Math.min(deptEmployees.length, empPerRow) * 230;

        // Nœud département (racine colorée de la branche)
        newNodes.push({
          id: `dept-${deptId}`,
          type: 'department',
          position: { x: currentX + deptWidth / 2 - 100, y: 0 },
          draggable: false,
          data: {
            name: deptName,
            count: deptEmployees.length,
            primaryColor: branch.color,
            branchBg: branch.bg,
            accentColor: branding.accentColor
          }
        });

        // Ajouter les employés sous ce département
        deptEmployees.forEach((emp, empIndex) => {
          const row = Math.floor(empIndex / empPerRow);
          const col = empIndex % empPerRow;
          const empX = currentX + col * 230;
          const empY = 140 + row * 160;

          newNodes.push({
            id: emp.id,
            type: 'employee',
            position: { x: empX, y: empY },
            draggable: true,
            data: {
              ...emp,
              department: deptName,
              site: getSiteName(emp.siteId),
              societe: getSocieteName(emp.societeId),
              onSelect: setSelectedEmployee,
              primaryColor: branding.primaryColor,
              accentColor: branding.accentColor,
              branchColor: branch.color,
              branchBg: branch.bg,
              hiddenSubordinates: 0
            }
          });

          // Lien vers le nœud département (couleur de branche)
          newEdges.push({
            id: `edge-dept-${deptId}-${emp.id}`,
            source: `dept-${deptId}`,
            target: emp.id,
            type: 'step',
            style: { stroke: branch.color, strokeWidth: 2, opacity: 0.75 },
          });

          // Lien vers manager principal (si existe et visible)
          if (emp.managerId && employeeMap.has(emp.managerId)) {
            newEdges.push({
              id: `edge-mgr-${emp.managerId}-${emp.id}`,
              source: emp.managerId,
              target: emp.id,
              type: 'step',
              style: { stroke: branding.primaryColor, strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: branding.primaryColor }
            });
          }

          // Lien vers manager de site (si existe et visible)
          if (emp.siteManagerId && employeeMap.has(emp.siteManagerId) && emp.siteManagerId !== emp.managerId) {
            newEdges.push({
              id: `edge-site-${emp.siteManagerId}-${emp.id}`,
              source: emp.siteManagerId,
              target: emp.id,
              type: 'step',
              style: { stroke: branding.secondaryColor || '#94a3b8', strokeWidth: 2, strokeDasharray: '5,5' },
              markerEnd: { type: MarkerType.ArrowClosed, color: branding.secondaryColor || '#94a3b8' }
            });
          }
        });

        currentX += deptWidth + deptNodeSpacing;
      });

      // Employés sans département
      if (employeesWithoutDept.length > 0) {
        newNodes.push({
          id: 'dept-none',
          type: 'department',
          position: { x: currentX + 100, y: 0 },
          draggable: false,
          data: {
            name: 'Sans département',
            count: employeesWithoutDept.length,
            primaryColor: '#94a3b8',
            accentColor: '#e2e8f0'
          }
        });

        employeesWithoutDept.forEach((emp, empIndex) => {
          const row = Math.floor(empIndex / empPerRow);
          const col = empIndex % empPerRow;

          newNodes.push({
            id: emp.id,
            type: 'employee',
            position: { x: currentX + col * 230, y: 140 + row * 160 },  // Match new spacing
            draggable: true,
            data: {
              ...emp,
              department: 'Sans département',
              site: getSiteName(emp.siteId),
              societe: getSocieteName(emp.societeId),
              onSelect: setSelectedEmployee,
              primaryColor: branding.primaryColor,
              accentColor: branding.accentColor,
              branchColor: getBranchStyle().color,
              branchBg: getBranchStyle().bg,
              hiddenSubordinates: 0
            }
          });

          newEdges.push({
            id: `edge-dept-none-${emp.id}`,
            source: 'dept-none',
            target: emp.id,
            type: 'step',
            style: { stroke: '#64748b', strokeWidth: 2, opacity: 0.7 },
          });
        });
      }

      commitNodes(newNodes);
      setEdges(newEdges);
      return;
    }

    // Mode hiérarchique normal (quand il y a peu de racines ou filtre département actif)
    const processLevel = (levelEmployees, level = 0, startX = 0) => {
      // Stop if max depth reached
      if (maxDepthLimit > 0 && level >= maxDepthLimit) {
        return { width: 0 };
      }

      let currentX = startX;
      
      levelEmployees.forEach((employee) => {
        const children = filteredEmployees.filter(e => e.managerId === employee.id);
        const nodeWidth = Math.max(1, children.length) * nodeSpacing.x;
        
        // Process children first to calculate their width (only if within depth limit)
        let childrenWidth = 0;
        if (children.length > 0 && (maxDepthLimit === 0 || level + 1 < maxDepthLimit)) {
          const childResult = processLevel(children, level + 1, currentX);
          childrenWidth = childResult.width;
        }
        
        const nodeX = currentX + Math.max(nodeWidth, childrenWidth) / 2 - nodeSpacing.x / 2;
        
        // Add badge showing how many subordinates are hidden
        const hiddenCount = maxDepthLimit > 0 && level + 1 >= maxDepthLimit && children.length > 0 
          ? children.length 
          : 0;
        
        newNodes.push({
          id: employee.id,
          type: 'employee',
          position: { x: nodeX, y: level * nodeSpacing.y },
          draggable: true,
          data: {
            ...employee,
            department: getDepartmentName(employee.departmentId),
            site: getSiteName(employee.siteId),
            societe: getSocieteName(employee.societeId),
            onSelect: setSelectedEmployee,
            primaryColor: branding.primaryColor,
            accentColor: branding.accentColor,
            branchColor: getBranchStyle().color,
            branchBg: getBranchStyle().bg,
            hiddenSubordinates: hiddenCount
          }
        });

        // Create edge to manager (ligne pleine, couleur de branche)
        if (employee.managerId && employeeMap.has(employee.managerId)) {
          const branchColor = getBranchStyle().color;
          newEdges.push({
            id: `edge-${employee.managerId}-${employee.id}`,
            source: employee.managerId,
            target: employee.id,
            sourceHandle: null,
            targetHandle: null,
            type: 'step',
            style: { stroke: branchColor, strokeWidth: 2.5 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: branchColor,
            }
          });
        }

        // Create edge to site manager (ligne pointillée)
        if (employee.siteManagerId && employeeMap.has(employee.siteManagerId) && employee.siteManagerId !== employee.managerId) {
          newEdges.push({
            id: `edge-site-${employee.siteManagerId}-${employee.id}`,
            source: employee.siteManagerId,
            target: employee.id,
            sourceHandle: null,
            targetHandle: null,
            type: 'step',
            style: { 
              stroke: branding.secondaryColor || '#94a3b8', 
              strokeWidth: 2,
              strokeDasharray: '5,5'  // Ligne pointillée
            },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: branding.secondaryColor || '#94a3b8',
            },
            animated: false,
            label: 'Site',
            labelStyle: { fontSize: 10, fill: branding.secondaryColor || '#94a3b8' },
            labelBgStyle: { fill: 'white', fillOpacity: 0.8 }
          });
        }

        currentX += Math.max(nodeWidth, childrenWidth);
      });

      return { width: currentX - startX };
    };

    processLevel(rootEmployees);

    // Note: le nom du département / site / société filtré est affiché en TITRE H2
    // au-dessus du canvas ReactFlow (pas de nœud au milieu qui occupe de la place).

    commitNodes(newNodes);
    setEdges(newEdges);
  }, [employees, departments, sites, societes, filters.departmentId, filters.siteId, filters.societeId, filters.maxDepth, filters.hierarchyLevel, filters.spacing, getDepartmentName, getSiteName, getSocieteName, setNodes, setEdges, setSelectedEmployee, branding]);

  useEffect(() => {
    if (employees.length > 0) {
      buildOrgChart();
    }
  }, [buildOrgChart, employees.length, filters.spacing]);

  // Handle drag start
  const onNodeDragStart = useCallback((event, node) => {
    setDraggedNode(node);
  }, []);

  // Handle drag over a node (to show drop indicator)
  const onNodeDrag = useCallback((event, node) => {
    const targetNode = nodes.find(n => {
      if (n.id === node.id) return false;
      const dx = Math.abs(n.position.x - node.position.x);
      const dy = Math.abs(n.position.y - node.position.y);
      return dx < 100 && dy < 75;
    });
    
    setDropTarget(targetNode?.id || null);
  }, [nodes]);

  // Handle drop on another node
  const onNodeDragStop = useCallback((event, node) => {
    // Si la carte a été relâchée sur une autre carte → changement de manager.
    if (dropTarget && dropTarget !== node.id) {
      const draggedEmployee = employees.find(e => e.id === node.id);
      const targetEmployee = employees.find(e => e.id === dropTarget);
      
      if (draggedEmployee && targetEmployee) {
        // Check for circular reference
        const wouldCreateCircle = (employeeId, newManagerId) => {
          let current = newManagerId;
          while (current) {
            if (current === employeeId) return true;
            const emp = employees.find(e => e.id === current);
            current = emp?.managerId;
          }
          return false;
        };

        if (wouldCreateCircle(node.id, dropTarget)) {
          toast.error('Cette action créerait une référence circulaire');
          buildOrgChart();
        } else {
          setConfirmDialog({
            employee: draggedEmployee,
            newManager: targetEmployee
          });
        }
      }
      setDraggedNode(null);
      setDropTarget(null);
      buildOrgChart();
      return;
    }

    // Sinon : simple repositionnement manuel → persister la nouvelle position.
    setDraggedNode(null);
    setDropTarget(null);
    if (onCustomPositionsChange && node?.position) {
      onCustomPositionsChange({
        [node.id]: { x: node.position.x, y: node.position.y }
      });
    }
  }, [dropTarget, employees, buildOrgChart, onCustomPositionsChange]);

  const handleConfirmManagerChange = async () => {
    if (!confirmDialog) return;
    
    try {
      await onManagerChange(confirmDialog.employee.id, confirmDialog.newManager.id);
      toast.success(`${confirmDialog.employee.firstName} ${confirmDialog.employee.lastName} reportera désormais à ${confirmDialog.newManager.firstName} ${confirmDialog.newManager.lastName}`);
    } catch (error) {
      toast.error('Erreur lors du changement de manager');
    }
    
    setConfirmDialog(null);
  };

  // Capture l'organigramme avec html2canvas et retourne un canvas
  const captureOrgChart = async () => {
    // Cible le viewport React Flow qui contient les noeuds + edges
    const element = document.querySelector('.react-flow__viewport') || document.getElementById('orgchart-container');
    if (!element) return null;

    // html-to-image capture correctement les SVG edges (contrairement à html2canvas)
    // On utilise toPng puis on charge dans un canvas pour garder la suite (bandeau)
    const reactFlowEl = document.querySelector('.react-flow');
    const width = reactFlowEl?.scrollWidth || element.scrollWidth;
    const height = reactFlowEl?.scrollHeight || element.scrollHeight;

    // Calculer les bornes réelles des nœuds pour éviter les marges inutiles
    const bounds = nodes.reduce(
      (acc, n) => {
        const x = n.position?.x || 0;
        const y = n.position?.y || 0;
        const w = n.width || 280;
        const h = n.height || 180;
        return {
          minX: Math.min(acc.minX, x),
          minY: Math.min(acc.minY, y),
          maxX: Math.max(acc.maxX, x + w),
          maxY: Math.max(acc.maxY, y + h),
        };
      },
      { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
    );
    const pad = 60;
    const viewWidth = isFinite(bounds.maxX) ? (bounds.maxX - bounds.minX) + pad * 2 : width;
    const viewHeight = isFinite(bounds.maxY) ? (bounds.maxY - bounds.minY) + pad * 2 : height;

    try {
      // Force visibility of edges
      const style = document.createElement('style');
      style.id = 'org-export-style';
      style.textContent = `
        .react-flow__edge-path {
          stroke: #64748b !important;
          stroke-width: 2.5px !important;
        }
        .react-flow__controls, .react-flow__minimap, .react-flow__attribution {
          display: none !important;
        }
      `;
      document.head.appendChild(style);

      // Reset viewport pour éviter le zoom / translation courants
      const dataUrl = await toPng(element, {
        backgroundColor: '#ffffff',
        pixelRatio: 1.5,
        width: viewWidth,
        height: viewHeight,
        style: {
          transform: `translate(${-bounds.minX + pad}px, ${-bounds.minY + pad}px) scale(1)`,
          transformOrigin: '0 0',
          width: `${viewWidth}px`,
          height: `${viewHeight}px`,
        },
        filter: (node) => {
          if (node.classList?.contains('react-flow__controls')) return false;
          if (node.classList?.contains('react-flow__minimap')) return false;
          if (node.classList?.contains('react-flow__attribution')) return false;
          return true;
        },
      });

      document.getElementById('org-export-style')?.remove();

      // Charger dans un canvas
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = img.width;
          c.height = img.height;
          const ctx = c.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, c.width, c.height);
          ctx.drawImage(img, 0, 0);
          resolve(c);
        };
        img.src = dataUrl;
      });
    } catch (e) {
      document.getElementById('org-export-style')?.remove();
      console.error('toPng failed, falling back to html2canvas:', e);
      return await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true
      });
    }
  };

  // Libellés de contexte affichés dans le bandeau (filtres actifs)
  const getFilterContext = () => {
    const parts = [];
    if (filters.departmentId && filters.departmentId !== 'all') {
      const d = departments.find(x => x.id === filters.departmentId);
      if (d) parts.push(`Département : ${d.name}`);
    }
    if (filters.siteId && filters.siteId !== 'all') {
      const s = sites.find(x => x.id === filters.siteId);
      if (s) parts.push(`Site : ${s.name}`);
    }
    if (filters.societeId && filters.societeId !== 'all') {
      const so = societes.find(x => x.id === filters.societeId);
      if (so) parts.push(`Société : ${so.name}`);
    }
    return parts.length ? parts.join(' · ') : 'Vue complète · tous départements & sites';
  };

  // Construit un canvas final avec un bandeau charté en haut
  const buildBrandedCanvas = async (orgCanvas) => {
    const headerHeight = 200;
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = orgCanvas.width;
    finalCanvas.height = orgCanvas.height + headerHeight;
    const ctx = finalCanvas.getContext('2d');

    // Fond blanc
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    // Bandeau d'en-tête avec couleur primaire de la charte
    const primary = branding.primaryColor || '#2054A6';
    ctx.fillStyle = primary;
    ctx.fillRect(0, 0, finalCanvas.width, headerHeight);

    // Bande accent
    const accent = branding.accentColor || '#F97316';
    ctx.fillStyle = accent;
    ctx.fillRect(0, headerHeight - 8, finalCanvas.width, 8);

    // Logo
    let textX = 48;
    if (branding.logoUrl) {
      try {
        const logo = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = branding.logoUrl;
        });
        const logoH = 120;
        const logoW = (logo.width / logo.height) * logoH;
        ctx.fillStyle = '#ffffff';
        const padding = 12;
        ctx.fillRect(48 - padding / 2, (headerHeight - logoH) / 2 - padding / 2, logoW + padding, logoH + padding);
        ctx.drawImage(logo, 48, (headerHeight - logoH) / 2, logoW, logoH);
        textX = 48 + logoW + 32;
      } catch (e) { /* continue sans logo */ }
    }

    // Titre, société, filtres
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 46px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillText('Organigramme', textX, 60);

    ctx.font = '28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const companyName = branding.companyDisplayName || branding.companyName || '';
    if (companyName) {
      ctx.fillText(companyName, textX, 108);
    }

    // Contexte (filtres département/site)
    ctx.font = '22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.globalAlpha = 0.9;
    ctx.fillText(getFilterContext(), textX, 150);
    ctx.globalAlpha = 1;

    // Date en haut à droite
    ctx.textAlign = 'right';
    ctx.font = '22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    ctx.fillText(dateStr, finalCanvas.width - 48, headerHeight / 2);
    ctx.textAlign = 'start';

    // Organigramme
    ctx.drawImage(orgCanvas, 0, headerHeight);

    return finalCanvas;
  };

  // Export functions
  const handleExportPNG = async () => {
    try {
      const orgCanvas = await captureOrgChart();
      if (!orgCanvas) return;
      const final = await buildBrandedCanvas(orgCanvas);
      const safeName = (branding.companyDisplayName || 'organigramme').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const link = document.createElement('a');
      link.download = `organigramme-${safeName}.png`;
      link.href = final.toDataURL('image/png');
      link.click();
      toast.success('PNG exporté avec succès');
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de l\'export PNG');
    }
  };

  // Export PDF — remplit toujours la page au maximum (impression haute qualité)
  // mode: 'auto' | 'single' | 'multi'
  // paperFormat: 'a4' | 'a3' | undefined (auto)
  // orientation: 'landscape' | 'portrait' | undefined (auto)
  const handleExportPDF = async (mode = 'auto', paperFormat, orientation) => {
    try {
      const orgCanvas = await captureOrgChart();
      if (!orgCanvas) return;
      const final = await buildBrandedCanvas(orgCanvas);

      const aspect = final.width / final.height;

      // 1) Format papier : utilisateur > auto (A3 si très large, sinon A4)
      const useA3 = paperFormat === 'a3' || (
        !paperFormat && (aspect > 1.8 || final.width > 3500)
      );
      const format = useA3 ? 'a3' : 'a4';

      // 2) Orientation : utilisateur > auto (paysage si plus large que haut)
      const isLandscape = orientation
        ? orientation === 'landscape'
        : aspect >= 1;

      // Dimensions en mm
      // A4 : 297 × 210 (paysage) / 210 × 297 (portrait)
      // A3 : 420 × 297 (paysage) / 297 × 420 (portrait)
      const a4Long = 297, a4Short = 210;
      const a3Long = 420, a3Short = 297;
      const pageWidthMm = useA3
        ? (isLandscape ? a3Long : a3Short)
        : (isLandscape ? a4Long : a4Short);
      const pageHeightMm = useA3
        ? (isLandscape ? a3Short : a3Long)
        : (isLandscape ? a4Short : a4Long);

      // Marges réduites pour maximiser le remplissage (impression standard 5 mm)
      const marginMm = 5;
      const usableWidthMm = pageWidthMm - 2 * marginMm;
      const usableHeightMm = pageHeightMm - 2 * marginMm;

      const pdf = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format,
        compress: true,
      });

      // DPI cible pour lisibilité (200 DPI = qualité impression)
      const targetDpi = 200;
      const pxPerMm = targetDpi / 25.4;
      const canvasWidthMm = final.width / pxPerMm;
      const canvasHeightMm = final.height / pxPerMm;

      // Échelle nécessaire pour tenir sur une seule page (en gardant les proportions)
      const scaleToFitSingle = Math.min(
        usableWidthMm / canvasWidthMm,
        usableHeightMm / canvasHeightMm
      );

      // Pagination uniquement si l'utilisateur l'a explicitement demandée OU si scaling
      // descend sous 0.35 en mode auto (en dessous = vraiment illisible)
      const shouldUseMulti =
        mode === 'multi' || (mode === 'auto' && scaleToFitSingle < 0.35);

      if (!shouldUseMulti) {
        // 1 page — REMPLIR la page tout en gardant les proportions
        const drawWidthMm = canvasWidthMm * scaleToFitSingle;
        const drawHeightMm = canvasHeightMm * scaleToFitSingle;
        const offsetX = (pageWidthMm - drawWidthMm) / 2;
        const offsetY = (pageHeightMm - drawHeightMm) / 2;
        pdf.addImage(
          final.toDataURL('image/jpeg', 0.92),
          'JPEG',
          offsetX,
          offsetY,
          drawWidthMm,
          drawHeightMm,
          undefined,
          'FAST'
        );
      } else {
        // Multi-pages : découpe l'image en tuiles qui remplissent chaque page à 100%
        const tilePxWidth = Math.round(usableWidthMm * pxPerMm);
        const tilePxHeight = Math.round(usableHeightMm * pxPerMm);
        const cols = Math.ceil(final.width / tilePxWidth);
        const rows = Math.ceil(final.height / tilePxHeight);
        const total = cols * rows;

        let pageIdx = 0;
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (pageIdx > 0) pdf.addPage(format, isLandscape ? 'landscape' : 'portrait');
            pageIdx += 1;

            const sx = c * tilePxWidth;
            const sy = r * tilePxHeight;
            const sw = Math.min(tilePxWidth, final.width - sx);
            const sh = Math.min(tilePxHeight, final.height - sy);

            const tile = document.createElement('canvas');
            tile.width = sw;
            tile.height = sh;
            const tctx = tile.getContext('2d');
            tctx.fillStyle = '#ffffff';
            tctx.fillRect(0, 0, sw, sh);
            tctx.drawImage(final, sx, sy, sw, sh, 0, 0, sw, sh);

            const drawWidthMm = sw / pxPerMm;
            const drawHeightMm = sh / pxPerMm;
            pdf.addImage(
              tile.toDataURL('image/jpeg', 0.92),
              'JPEG',
              marginMm,
              marginMm,
              drawWidthMm,
              drawHeightMm,
              undefined,
              'FAST'
            );

            pdf.setFontSize(9);
            pdf.setTextColor(120);
            pdf.text(
              `Page ${pageIdx} / ${total}`,
              pageWidthMm - marginMm,
              pageHeightMm - 2,
              { align: 'right' }
            );
          }
        }
      }

      const safeName = (branding.companyDisplayName || 'organigramme')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
      const formatLabel = format.toUpperCase();
      pdf.save(`organigramme-${safeName}-${formatLabel}.pdf`);
      toast.success(
        shouldUseMulti
          ? `PDF ${formatLabel} exporté (pagination automatique)`
          : `PDF ${formatLabel} exporté avec succès`
      );
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const handleExportCSV = () => {
    try {
      // Filter employees based on current filters
      let filteredEmployees = employees.filter(e => e.status === 'Actif');
      
      if (filters.departmentId) {
        filteredEmployees = filteredEmployees.filter(e => e.departmentId === filters.departmentId);
      }
      if (filters.siteId) {
        filteredEmployees = filteredEmployees.filter(e => e.siteId === filters.siteId);
      }

      // Build CSV
      const headers = ['Nom', 'Prénom', 'Email', 'Poste', 'Département', 'Site', 'Manager'];
      
      const rows = filteredEmployees.map(emp => {
        const manager = employees.find(e => e.id === emp.managerId);
        const managerName = manager ? `${manager.firstName} ${manager.lastName}` : '';
        
        return [
          emp.lastName || '',
          emp.firstName || '',
          emp.email || '',
          emp.jobTitle || '',
          getDepartmentName(emp.departmentId),
          getSiteName(emp.siteId),
          managerName
        ];
      });

      const csvContent = [
        headers.join(';'),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
      ].join('\n');

      // Add BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'organigramme-webtech.csv';
      link.click();
      
      toast.success('CSV exporté avec succès');
    } catch (error) {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  return (
    <>
      {/* Row 1 : Titre + Exporter (toujours visible) */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-4 min-w-0">
          {branding.logoUrl && (
            <img 
              src={branding.logoUrl} 
              alt="Logo" 
              className="h-10 object-contain flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              Organigramme
            </h1>
            <p className="text-slate-600 text-sm flex items-center gap-2">
              <Move className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">Glissez-déposez un employé sur un autre pour changer son manager</span>
            </p>
          </div>
        </div>

        {/* Export dropdown — toujours visible en haut à droite */}
        <div className="flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                style={{ backgroundColor: branding.primaryColor }}
                className="hover:opacity-90 text-white"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleExportPNG} className="cursor-pointer">
                <FileImage className="w-4 h-4 mr-2 text-green-600" />
                Export PNG
              </DropdownMenuItem>

              {/* PDF — A4 (défaut) */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer" data-testid="export-pdf-a4-trigger">
                  <FileText className="w-4 h-4 mr-2 text-red-600" />
                  Export PDF — A4 (Défaut)
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-56">
                    <DropdownMenuItem
                      onClick={() => handleExportPDF('auto', 'a4')}
                      className="cursor-pointer"
                      data-testid="export-pdf-a4-auto"
                    >
                      Auto (recommandé)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportPDF('single', 'a4')}
                      className="cursor-pointer"
                      data-testid="export-pdf-a4-single"
                    >
                      1 page (rempli)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportPDF('multi', 'a4')}
                      className="cursor-pointer"
                      data-testid="export-pdf-a4-multi"
                    >
                      Plusieurs pages
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              {/* PDF — A3 */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="cursor-pointer" data-testid="export-pdf-a3-trigger">
                  <FileText className="w-4 h-4 mr-2 text-red-700" />
                  Export PDF — A3 (Grand format)
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-56">
                    <DropdownMenuItem
                      onClick={() => handleExportPDF('auto', 'a3')}
                      className="cursor-pointer"
                      data-testid="export-pdf-a3-auto"
                    >
                      Auto (recommandé)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportPDF('single', 'a3')}
                      className="cursor-pointer"
                      data-testid="export-pdf-a3-single"
                    >
                      1 page (rempli)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleExportPDF('multi', 'a3')}
                      className="cursor-pointer"
                      data-testid="export-pdf-a3-multi"
                    >
                      Plusieurs pages
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportCSV} className="cursor-pointer" data-testid="export-csv">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Export CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Row 2 : Filtres — peuvent wrapper sans cacher l'export */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Société filter */}
          <Select
            value={filters.societeId || 'all'}
            onValueChange={(value) => filters.setSocieteId(value === 'all' ? '' : value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Société" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les sociétés</SelectItem>
              {societes.map((soc) => (
                <SelectItem key={soc.id} value={soc.id}>{soc.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Site filter */}
          <Select
            value={filters.siteId || 'all'}
            onValueChange={(value) => filters.setSiteId(value === 'all' ? '' : value)}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les sites</SelectItem>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Department filter */}
          <Select
            value={filters.departmentId || 'all'}
            onValueChange={(value) => filters.setDepartmentId(value === 'all' ? '' : value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Département" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les départements</SelectItem>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Max depth filter */}
          <Select
            value={String(filters.maxDepth || 0)}
            onValueChange={(value) => filters.setMaxDepth(parseInt(value))}
          >
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Profondeur" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Tous les niveaux</SelectItem>
              <SelectItem value="1">1 niveau (N)</SelectItem>
              <SelectItem value="2">2 niveaux (N+1)</SelectItem>
              <SelectItem value="3">3 niveaux (N+2)</SelectItem>
              <SelectItem value="4">4 niveaux (N+3)</SelectItem>
              <SelectItem value="5">5 niveaux (N+4)</SelectItem>
            </SelectContent>
          </Select>

          {/* Hierarchy level filter */}
          <Select
            value={String(filters.hierarchyLevel || 0)}
            onValueChange={(value) => filters.setHierarchyLevel(parseInt(value))}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Rang hiérarchique" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Tous les rangs</SelectItem>
              {filters.availableLevels?.map(level => (
                <SelectItem key={level} value={String(level)}>
                  {filters.getLevelLabel?.(level) || `Niveau ${level}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Slider espacement */}
          <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2 shadow-sm" data-testid="spacing-slider-container">
            <span className="text-xs font-medium text-slate-600 whitespace-nowrap">Espacement</span>
            <input
              type="range"
              min="0.5"
              max="2.5"
              step="0.25"
              value={filters.spacing || 1}
              onChange={(e) => filters.setSpacing(parseFloat(e.target.value))}
              className="w-24 cursor-pointer"
              style={{ accentColor: branding.primaryColor }}
              data-testid="spacing-slider"
            />
            <span className="text-xs text-slate-500 font-mono w-10 text-right">
              ×{Number(filters.spacing || 1).toFixed(2)}
            </span>
          </div>

          {/* Reset layout custom button */}
          {Object.keys(customPositions || {}).length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onResetLayout?.()}
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              data-testid="reset-layout-btn"
              title="Remet les positions calculées automatiquement (supprime les déplacements manuels)"
            >
              <Move className="w-4 h-4 mr-1" />
              Disposition auto
            </Button>
          )}

          {/* Clear filters button */}
          {(filters.departmentId || filters.siteId || filters.societeId || filters.maxDepth > 0 || filters.hierarchyLevel > 0 || (filters.spacing && filters.spacing !== 1)) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                filters.setDepartmentId('');
                filters.setSiteId('');
                filters.setSocieteId('');
                filters.setMaxDepth(0);
                filters.setHierarchyLevel(0);
                filters.setSpacing?.(1);
              }}
            >
              <X className="w-4 h-4 mr-1" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Message d'information sur le mode de regroupement */}
      {(() => {
        let filteredEmps = employees.filter(e => e.status === 'Actif');
        if (filters.departmentId) filteredEmps = filteredEmps.filter(e => e.departmentId === filters.departmentId);
        if (filters.siteId) filteredEmps = filteredEmps.filter(e => e.siteId === filters.siteId);
        if (filters.societeId) filteredEmps = filteredEmps.filter(e => e.societeId === filters.societeId);
        
        const employeeMap = new Map(filteredEmps.map(e => [e.id, e]));
        const rootCount = filteredEmps.filter(e => !e.managerId || !employeeMap.has(e.managerId)).length;
        const hasFilter = filters.departmentId || filters.siteId || filters.societeId;
        const useDeptGrouping = rootCount > 10 && !hasFilter;
        const useSimpleGrid = rootCount > 10 && hasFilter;
        
        if (useDeptGrouping) {
          return (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Mode regroupement par département</p>
                <p className="text-sm text-blue-600">
                  La plupart des employés n'ont pas de manager assigné. L'organigramme est regroupé par département.
                  Pour afficher la hiérarchie managériale, assignez des managers aux employés.
                </p>
              </div>
            </div>
          );
        }
        
        if (useSimpleGrid) {
          return (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Mode grille - {filteredEmps.length} employé{filteredEmps.length > 1 ? 's' : ''}</p>
                <p className="text-sm text-amber-600">
                  Les employés sont affichés en grille car ils n'ont pas de manager assigné.
                  Pour afficher la hiérarchie, assignez des managers aux employés.
                </p>
              </div>
            </div>
          );
        }

        // Message pour le filtre par niveau hiérarchique
        if (filters.hierarchyLevel > 0) {
          const levelLabel = filters.getLevelLabel?.(filters.hierarchyLevel) || `Niveau ${filters.hierarchyLevel}`;
          return (
            <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3">
              <Users className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-purple-800">{levelLabel} - {filteredEmps.length} employé{filteredEmps.length > 1 ? 's' : ''}</p>
                <p className="text-sm text-purple-600">
                  Affichage des employés du rang hiérarchique sélectionné.
                </p>
              </div>
            </div>
          );
        }
        
        return null;
      })()}

      <Card className="h-[70vh] overflow-hidden flex flex-col">
        {/* Branded header for exports */}
        <div 
          className="p-4 flex items-center justify-between flex-shrink-0"
          style={{ backgroundColor: branding.primaryColor }}
        >
          <div className="flex items-center gap-3">
            {branding.logoUrl ? (
              <img 
                src={branding.logoUrl} 
                alt="Logo" 
                className="h-8 object-contain bg-white rounded px-2 py-1"
              />
            ) : (
              <Building2 className="w-6 h-6 text-white" />
            )}
            <span className="text-white font-semibold">
              {branding.companyDisplayName || 'Organigramme'}
            </span>
          </div>
          <div className="text-white text-right">
            <span className="text-sm font-medium uppercase tracking-wider">Organigramme</span>
          </div>
        </div>
        <div 
          className="h-1 flex-shrink-0"
          style={{ backgroundColor: branding.accentColor }}
        />

        {/* Titre hiérarchique : nom du département / site / société filtré */}
        {(filters.departmentId || filters.siteId || filters.societeId) && (
          <div
            className="px-6 py-3 border-b bg-white flex items-center justify-between flex-shrink-0"
            style={{ borderColor: `${branding.primaryColor}20` }}
          >
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">
                {filters.departmentId ? 'Département' : filters.siteId ? 'Site' : 'Société'}
              </p>
              <h2
                className="text-xl font-bold"
                style={{ color: branding.primaryColor }}
              >
                {filters.departmentId
                  ? (departments.find(d => d.id === filters.departmentId)?.name || '-')
                  : filters.siteId
                    ? (sites.find(s => s.id === filters.siteId)?.name || '-')
                    : (societes.find(so => so.id === filters.societeId)?.name || '-')}
              </h2>
            </div>
            <Badge
              className="text-sm"
              style={{ backgroundColor: branding.primaryColor, color: 'white' }}
            >
              {nodes.filter(n => n.type === 'employee').length} employé{nodes.filter(n => n.type === 'employee').length > 1 ? 's' : ''}
            </Badge>
          </div>
        )}

        <div id="orgchart-container" className="flex-1 min-h-0" ref={flowRef}>
          <ReactFlow
            nodes={nodes.map(n => ({
              ...n,
              style: dropTarget === n.id ? { 
                outline: '3px dashed #2054A6',
                outlineOffset: '4px',
                borderRadius: '12px'
              } : {}
            }))}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStart={onNodeDragStart}
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
            minZoom={0.05}
            maxZoom={2}
            defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
            nodesDraggable={true}
            elementsSelectable={true}
            panOnDrag={[1, 2]}
            proOptions={{ hideAttribution: true }}
            nodeOrigin={[0.5, 0]}
            connectionMode="loose"
            snapToGrid={false}
            deleteKeyCode={null}
            selectionKeyCode={null}
            multiSelectionKeyCode={null}
          >
            <Background color="#e2e8f0" gap={20} />
            <Controls 
              className="bg-white border border-slate-200 rounded-lg shadow" 
              showInteractive={false}
            />
          </ReactFlow>
        </div>
      </Card>

      {nodes.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500">
            Aucun employé à afficher. Ajoutez des employés avec des relations hiérarchiques pour voir l'organigramme.
          </p>
        </div>
      )}

      {/* Confirm Manager Change Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le changement de manager</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Voulez-vous vraiment changer le manager de{' '}
                <strong>{confirmDialog?.employee?.firstName} {confirmDialog?.employee?.lastName}</strong> ?
              </p>
              <div className="bg-slate-50 p-3 rounded-lg mt-3">
                <p className="text-sm text-slate-600">
                  Nouveau manager:{' '}
                  <strong className="text-webtech-blue">
                    {confirmDialog?.newManager?.firstName} {confirmDialog?.newManager?.lastName}
                  </strong>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmManagerChange}
              className="bg-webtech-blue hover:bg-webtech-blue-hover"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function OrgChartPage() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [societeFilter, setSocieteFilter] = useState('');
  const [maxDepth, setMaxDepth] = useState(0); // 0 = unlimited
  const [hierarchyLevel, setHierarchyLevel] = useState(0); // 0 = tous les niveaux
  const [spacing, setSpacing] = useState(1); // 0.5 = serré, 1 = normal, 1.5 / 2 = aéré
  const [societes, setSocietes] = useState([]);
  const [branding, setBranding] = useState({
    primaryColor: '#2054A6',
    accentColor: '#F97316',
    logoUrl: null,
    companyDisplayName: ''
  });
  // Positions manuelles (drag & drop sauvegardé) par employeeId
  const [customPositions, setCustomPositions] = useState({});
  // Debounce des sauvegardes pour ne pas spammer l'API pendant le drag rapide
  const saveTimerRef = useRef(null);
  const pendingSavesRef = useRef({});

  const flushLayoutSaves = useCallback(async () => {
    const pending = pendingSavesRef.current;
    pendingSavesRef.current = {};
    if (!pending || Object.keys(pending).length === 0) return;
    try {
      await api.put('/orgchart/layout', { positions: pending });
    } catch (e) {
      console.error('Save layout failed:', e);
      toast.error('Impossible de sauvegarder la disposition');
    }
  }, []);

  const handleCustomPositionsChange = useCallback((partial) => {
    setCustomPositions((prev) => ({ ...prev, ...partial }));
    pendingSavesRef.current = { ...pendingSavesRef.current, ...partial };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushLayoutSaves();
    }, 400);
  }, [flushLayoutSaves]);

  const handleResetLayout = useCallback(async () => {
    try {
      await api.delete('/orgchart/layout');
      setCustomPositions({});
      pendingSavesRef.current = {};
      toast.success('Disposition réinitialisée');
    } catch (e) {
      toast.error('Erreur lors de la réinitialisation');
    }
  }, []);

  // Fonction pour calculer le niveau hiérarchique d'un employé
  const calculateHierarchyLevel = useCallback((employeeId, employeesMap, visited = new Set()) => {
    if (visited.has(employeeId)) return 1; // Éviter les boucles
    visited.add(employeeId);
    
    const employee = employeesMap.get(employeeId);
    if (!employee || !employee.managerId) return 1; // Niveau 1 = Top
    
    const manager = employeesMap.get(employee.managerId);
    if (!manager) return 1;
    
    return 1 + calculateHierarchyLevel(employee.managerId, employeesMap, visited);
  }, []);

  // Calculer les niveaux pour tous les employés
  const employeesWithLevels = useMemo(() => {
    const employeesMap = new Map(employees.map(e => [e.id, e]));
    return employees.map(emp => ({
      ...emp,
      hierarchyLevel: calculateHierarchyLevel(emp.id, employeesMap)
    }));
  }, [employees, calculateHierarchyLevel]);

  // Obtenir les niveaux uniques disponibles
  const availableLevels = useMemo(() => {
    const levels = new Set(employeesWithLevels.map(e => e.hierarchyLevel));
    return Array.from(levels).sort((a, b) => a - b);
  }, [employeesWithLevels]);

  // Labels pour les niveaux
  const getLevelLabel = (level) => {
    const labels = {
      1: 'Niveau 1 - Direction Générale',
      2: 'Niveau 2 - Directeurs',
      3: 'Niveau 3 - Responsables',
      4: 'Niveau 4 - Managers',
      5: 'Niveau 5 - Superviseurs'
    };
    return labels[level] || `Niveau ${level}`;
  };

  useEffect(() => {
    fetchData();
    fetchBranding();
    fetchLayout();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, deptRes, siteRes, societeRes] = await Promise.all([
        api.get('/employees/all'),
        api.get('/departments'),
        api.get('/sites'),
        api.get('/societes')
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data.departments || deptRes.data || []);
      setSites(siteRes.data || []);
      setSocietes(societeRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await api.get('/branding');
      setBranding(response.data);
    } catch (error) {
      console.error('Could not load branding:', error);
    }
  };

  const fetchLayout = async () => {
    try {
      const response = await api.get('/orgchart/layout');
      setCustomPositions(response.data?.positions || {});
    } catch (error) {
      console.error('Could not load orgchart layout:', error);
    }
  };

  const handleManagerChange = async (employeeId, newManagerId) => {
    await api.patch(`/employees/${employeeId}`, { managerId: newManagerId });
    // Refresh data
    const empRes = await api.get('/employees/all');
    setEmployees(empRes.data);
  };

  if (loading) {
    return (
      <DashboardLayout title="Organigramme">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Organigramme">
      <div className="space-y-4" data-testid="orgchart-page">
        <ReactFlowProvider>
          <OrgChartFlow
            employees={employeesWithLevels}
            departments={departments}
            sites={sites}
            societes={societes}
            filters={{
              departmentId: departmentFilter,
              siteId: siteFilter,
              societeId: societeFilter,
              maxDepth: maxDepth,
              hierarchyLevel: hierarchyLevel,
              spacing: spacing,
              setDepartmentId: setDepartmentFilter,
              setSiteId: setSiteFilter,
              setSocieteId: setSocieteFilter,
              setMaxDepth: setMaxDepth,
              setHierarchyLevel: setHierarchyLevel,
              setSpacing: setSpacing,
              availableLevels: availableLevels,
              getLevelLabel: getLevelLabel
            }}
            setSelectedEmployee={setSelectedEmployee}
            onManagerChange={handleManagerChange}
            branding={branding}
            customPositions={customPositions}
            onCustomPositionsChange={handleCustomPositionsChange}
            onResetLayout={handleResetLayout}
          />
        </ReactFlowProvider>

        {/* Employee Detail Sheet */}
        <Sheet open={!!selectedEmployee} onOpenChange={() => setSelectedEmployee(null)}>
          <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
            {selectedEmployee && (
              <>
                <SheetHeader>
                  <SheetTitle>Fiche employé</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-6">
                  {/* Profile Header */}
                  <div className="flex items-start gap-4">
                    <Avatar className="h-20 w-20 flex-shrink-0">
                      {selectedEmployee.photoUrl ? (
                        <AvatarImage src={selectedEmployee.photoUrl} />
                      ) : null}
                      <AvatarFallback 
                        className="text-white text-xl"
                        style={{ backgroundColor: branding.primaryColor }}
                      >
                        {`${selectedEmployee.firstName?.[0] || ''}${selectedEmployee.lastName?.[0] || ''}`}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl font-bold text-slate-900">
                        {selectedEmployee.firstName} {selectedEmployee.lastName}
                      </h3>
                      <p 
                        className="font-medium"
                        style={{ color: branding.primaryColor }}
                      >
                        {selectedEmployee.jobTitle}
                      </p>
                      {selectedEmployee.matricule && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Hash className="w-3 h-3" />
                          {selectedEmployee.matricule}
                        </p>
                      )}
                      <Badge 
                        className={`mt-2 ${
                          selectedEmployee.status === 'Actif' 
                            ? 'bg-green-100 text-green-700' 
                            : selectedEmployee.status === 'Inactif'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {selectedEmployee.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Contact Section */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      Contact
                    </h4>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                      {selectedEmployee.email ? (
                        <div className="flex items-center gap-3 text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <a href={`mailto:${selectedEmployee.email}`} className="hover:text-webtech-blue">
                            {selectedEmployee.email}
                          </a>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 text-slate-400">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className="italic">Email non renseigné</span>
                        </div>
                      )}
                      {selectedEmployee.phone && (
                        <div className="flex items-center gap-3 text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <a href={`tel:${selectedEmployee.phone}`} className="hover:text-webtech-blue">
                            {selectedEmployee.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Informations personnelles */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      Informations personnelles
                    </h4>
                    <div className="bg-slate-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-slate-400 uppercase">Sexe</span>
                        <p className="font-medium text-slate-700">
                          {selectedEmployee.sexe && selectedEmployee.sexe !== 'Non renseigné' 
                            ? selectedEmployee.sexe 
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 uppercase">Situation familiale</span>
                        <p className="font-medium text-slate-700">
                          {selectedEmployee.situationFamiliale && selectedEmployee.situationFamiliale !== 'Non renseigné' 
                            ? selectedEmployee.situationFamiliale 
                            : '-'}
                        </p>
                      </div>
                      {selectedEmployee.dateNaissance && (
                        <div className="col-span-2">
                          <span className="text-xs text-slate-400 uppercase">Date de naissance</span>
                          <p className="font-medium text-slate-700">
                            {new Date(selectedEmployee.dateNaissance).toLocaleDateString('fr-FR', { 
                              day: 'numeric', month: 'long', year: 'numeric' 
                            })}
                            {(() => {
                              const birth = new Date(selectedEmployee.dateNaissance);
                              const today = new Date();
                              let age = today.getFullYear() - birth.getFullYear();
                              const m = today.getMonth() - birth.getMonth();
                              if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                              return <span className="text-slate-400 ml-2">({age} ans)</span>;
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Organisation */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      Organisation
                    </h4>
                    <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                      {selectedEmployee.department && (
                        <div className="flex items-center gap-3 text-slate-600">
                          <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span>{selectedEmployee.department}</span>
                        </div>
                      )}
                      {selectedEmployee.site && (
                        <div className="flex items-center gap-3 text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span>{selectedEmployee.site}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Manager Info */}
                  {selectedEmployee.managerId && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                        <UserCircle className="w-4 h-4 text-slate-400" />
                        Responsable hiérarchique
                      </h4>
                      <div className="bg-slate-50 rounded-lg p-4">
                        {(() => {
                          const manager = employees.find(e => e.id === selectedEmployee.managerId);
                          if (manager) {
                            return (
                              <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  {manager.photoUrl ? (
                                    <AvatarImage src={manager.photoUrl} />
                                  ) : null}
                                  <AvatarFallback className="bg-slate-200 text-slate-600 text-sm">
                                    {`${manager.firstName?.[0] || ''}${manager.lastName?.[0] || ''}`}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    {manager.firstName} {manager.lastName}
                                  </p>
                                  <p className="text-sm text-slate-500">{manager.jobTitle}</p>
                                </div>
                              </div>
                            );
                          }
                          return <span className="text-slate-400 italic">Non défini</span>;
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Manager de Site (si différent du manager principal) */}
                  {selectedEmployee.siteManagerId && selectedEmployee.siteManagerId !== selectedEmployee.managerId && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-blue-500" />
                        Manager de Site
                        <Badge variant="outline" className="text-xs ml-2 border-blue-200 text-blue-600">
                          Fonctionnel
                        </Badge>
                      </h4>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        {(() => {
                          const siteManager = employees.find(e => e.id === selectedEmployee.siteManagerId);
                          if (siteManager) {
                            return (
                              <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10 ring-2 ring-blue-200">
                                  {siteManager.photoUrl ? (
                                    <AvatarImage src={siteManager.photoUrl} />
                                  ) : null}
                                  <AvatarFallback className="bg-blue-100 text-blue-600 text-sm">
                                    {`${siteManager.firstName?.[0] || ''}${siteManager.lastName?.[0] || ''}`}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-slate-900">
                                    {siteManager.firstName} {siteManager.lastName}
                                  </p>
                                  <p className="text-sm text-blue-600">{siteManager.jobTitle}</p>
                                </div>
                              </div>
                            );
                          }
                          return <span className="text-slate-400 italic">Non défini</span>;
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Informations RH */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-slate-400" />
                      Informations RH
                    </h4>
                    <div className="bg-slate-50 rounded-lg p-4 grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-slate-400 uppercase">Type de contrat</span>
                        <p className="font-medium text-slate-700">
                          {selectedEmployee.typeContrat || '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-slate-400 uppercase">Catégorie</span>
                        <p className="font-medium text-slate-700">
                          {selectedEmployee.categorieProfessionnelle && selectedEmployee.categorieProfessionnelle !== 'Non renseigné' 
                            ? selectedEmployee.categorieProfessionnelle 
                            : '-'}
                        </p>
                      </div>
                      {selectedEmployee.hireDate && (
                        <div className="col-span-2">
                          <span className="text-xs text-slate-400 uppercase">Date d'embauche</span>
                          <p className="font-medium text-slate-700">
                            {new Date(selectedEmployee.hireDate).toLocaleDateString('fr-FR', { 
                              day: 'numeric', month: 'long', year: 'numeric' 
                            })}
                            {(() => {
                              const hire = new Date(selectedEmployee.hireDate);
                              const today = new Date();
                              const years = today.getFullYear() - hire.getFullYear();
                              const months = today.getMonth() - hire.getMonth();
                              if (years === 0) {
                                return <span className="text-slate-400 ml-2">({Math.max(0, months)} mois d'ancienneté)</span>;
                              }
                              return <span className="text-slate-400 ml-2">({years} an{years > 1 ? 's' : ''} d'ancienneté)</span>;
                            })()}
                          </p>
                        </div>
                      )}
                      {selectedEmployee.typeEntree && (
                        <div>
                          <span className="text-xs text-slate-400 uppercase">Type d'entrée</span>
                          <p className="font-medium text-slate-700">{selectedEmployee.typeEntree}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Équipe (subordonnés) */}
                  {(() => {
                    const subordinates = employees.filter(e => e.managerId === selectedEmployee.id && e.status === 'Actif');
                    if (subordinates.length === 0) return null;
                    return (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                          <Info className="w-4 h-4 text-slate-400" />
                          Équipe ({subordinates.length})
                        </h4>
                        <div className="bg-slate-50 rounded-lg p-4 space-y-2 max-h-40 overflow-y-auto">
                          {subordinates.map(sub => (
                            <div key={sub.id} className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                {sub.photoUrl ? (
                                  <AvatarImage src={sub.photoUrl} />
                                ) : null}
                                <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                                  {`${sub.firstName?.[0] || ''}${sub.lastName?.[0] || ''}`}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="text-sm font-medium text-slate-700">
                                  {sub.firstName} {sub.lastName}
                                </p>
                                <p className="text-xs text-slate-500">{sub.jobTitle}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
